import { useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLayout } from 'prodivers/layout';
import LogoWithClientName from 'components/LogoWithClientName';
import { useClientProfile } from 'prodivers/clientProfile';
import { generateDFSClientId } from 'utils';

// Define the shape of a button configuration
interface HeaderButton {
    type: string;
    label: string;
}

/**
 * Hook to manage the Layout Header state for client-specific pages
 */
export const useClientLayout = (
    showClientTitle: boolean = true,
    showTopNavigation: boolean = true,
    leftButton?: HeaderButton,
    onLeftButtonClick?: () => void,
): void => {
    const { initHeaderState, changeHeaderState } = useLayout();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        clientId,
        clientName,
        isProspect,
        isDataAccessible,
        externalId,
        dateOfDeath,
    } = useClientProfile() as {
        clientId: string | number;
        clientName: string;
        isProspect: boolean;
        isDataAccessible: boolean;
        externalId: string | number;
        dateOfDeath?: string | null;
    };

    const dfsClientId = generateDFSClientId(clientId);

    // Update accessibility status if it changes
    useEffect(() => {
        changeHeaderState({
            isDataAccessible,
        });
    }, [isDataAccessible, changeHeaderState]);

    // Initialize the full header state
    useEffect(() => {
        initHeaderState({
            clientName,
            dfsClientId,
            externalId,
            link: null,
            content: LogoWithClientName,
            contentProps: { clientName, dfsClientId, isLogoClickable: true },
            showClientTitle,
            showTopNavigation,
            leftButton: leftButton ?? {
                type: 'login',
                label: `${t('clientDashboard.close')} ${clientName}`,
            },
            onLeftButtonClick: onLeftButtonClick ?? (() => {
                navigate(`/clients${isProspect ? '?status=Prospects' : ''}`);
            }),
            rightButton: '',
            onRightButtonClick: null,
            isDataAccessible,
            dateOfDeath,
        });
    }, [
        clientName,
        isProspect,
        dfsClientId,
        externalId,
        initHeaderState,
        t,
        isDataAccessible,
        leftButton,
        onLeftButtonClick,
        dateOfDeath,
        showClientTitle,
        showTopNavigation,
        navigate,
    ]);
};
